import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionIcon from '@mui/icons-material/Description'; // Filing Type
import EventIcon from '@mui/icons-material/Event'; // Due Date
// Help icon
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import ExemptSubMenu from '@/components/nav/ExemptSubMenu';
import Form1120POLPopup from '@/components/ui/Form1120POLPopup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const Form1120pol = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // const [progress, setProgress] = useState(0);

  const images = [
    <img
      src="/assets/images/1120s-business-details-img.webp"
      key="first"
      alt="Corporation Details"
      id="loading1"
      className="details-images"
    />,
    <img
      src="/assets/images/1120s-income-img.webp"
      key="second"
      alt="Income and Expenses"
      className="details-images"
    />,
    <img
      src="/assets/images/1120s-shareholder-img.webp"
      key="third"
      alt="Shareholder Information"
      className="details-images"
    />,
    <img
      src="/assets/images/1120s-k-1allocation-img.webp"
      key="fourth"
      alt="K-1 Allocations"
      className="details-images"
    />,
    <img
      src="/assets/images/1120s-balance-sheet-img.webp"
      key="fifth"
      alt="Balance Sheet Details"
      className="details-images"
    />,
  ];
  const loader = ['loading1', 'loading2', 'loading3', 'loading4', 'loading5'];
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentIndex === images.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [currentIndex + 1]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentIndex === loader.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [currentIndex + 1]);
  // Handle scrolling to change images

  return (
    <AppLayout
      //  LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-File Form 1120-POL Online | IRS Political Tax Return"
          description="E-file IRS Form 1120-POL for political organizations with TaxZerone. Stay IRS compliant with secure submissions, accurate filings, and deadline management."
          keywords=""
          canonical="tax-exempt/e-file-irs-form-1120-pol/"
          jsonLdPricing="129.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="1062"
          ogShareImg="assets/images/exempt-share-img.png"
        />
      }
    >
      <ExemptSubMenu />
      <Form1120POLPopup />
      <Container maxWidth="xl" component="section">
        <Hidden mdUp smDown>
          <Box
            sx={{
              mb: -1.5,
              textAlign: 'center',
              mt: 0,
              position: 'relative',
            }}
          >
            <Box
              component="picture"
              sx={{
                position: { xs: 'relative', sm: 'absolute' },
                right: 20,
                top: { xs: 0, sm: 20 },
              }}
            >
              <source
                srcSet="/assets/images/ftb-logo.webp"
                className="mw-100"
                width={60}
                height={60}
              />
              <source
                srcSet="/assets/images/ftb-logo.png"
                className="mw-100"
                width={60}
                height={60}
              />
              <img
                src="/assets/images/ftb-logo.png"
                loading="lazy"
                alt="FTB e-file provider"
                className="mw-100"
                width={60}
                height={60}
              />
            </Box>
          </Box>
        </Hidden>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-File IRS{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 1120-POL</span>
                </span>{' '}
                Online
              </Typography>
              <Box
                component="ul"
                sx={{
                  mb: 2,
                  '& li': {
                    mb: 1.2,
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  },
                }}
              >
                <li>
                  Report political organization taxable income and income tax
                  liability under Section 527 and securely e-file IRS Form
                  1120-POL online with TaxZerone.
                </li>
                <li>
                  Supports extensions, related IRS forms, and electronic filing
                  requirements.
                </li>
              </Box>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mr: { xs: 0, md: 4 },
                    mb: { xs: 2, sm: 0 },
                  }}
                >
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1120pol`}
                  >
                    File Form 1120-POL Now
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/form-1120-pol-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/form-1120-pol-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/form-1120-pol-landing-img.png"
                  alt="E-File Form 1120-POL with TaxZerone"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/ca199-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/ca199-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/ca199-landing-img-sm.png"
                  alt="E-File CA Form 199 with TaxZerone"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 30, sm: 26, xs: 22 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                File Form 1120-POL online for just{' '}
                <Box
                  component="strong"
                  sx={{
                    color: '#ffe400',
                    fontSize: { xs: 30, sm: 36, md: 48, lg: 56 },
                  }}
                >
                  $49.99
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 15, md: 15, sm: 14, xs: 12 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                TaxZerone offers a secure, simple, and affordable way for
                political organizations to e-file Form 1120-POL online.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 250, md: 280 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              <Button
                size="large"
                variant="contained"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1120pol`}
                sx={{
                  boxShadow: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  color: '#0a7581',
                  '&:hover, &:focus, &:active': {
                    color: '#fff',
                    backgroundColor: '#172b6b',
                  },
                }}
              >
                E-File Now
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 0, mb: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                No additional or hidden cost for supporting schedules.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#ffffff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/what-is-form1120-pol-img.png" />
                  <source srcSet="/assets/images/what-is-form1120-pol-img.png" />
                  <img
                    src="/assets/images/what-is-form1120-pol-img.png"
                    alt="What Is Form 1120-POL?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={382}
                    height={550}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                What Is Form 1120-POL?
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                IRS Form 1120-POL is a federal income tax return used by
                political organizations to report taxable income and determine
                tax liability under{' '}
                <span style={{ fontWeight: 600 }}>
                  Internal Revenue Code Section 527
                </span>
                .
              </Typography>

              <Typography variant="body1" sx={{ mb: 2 }}>
                It is primarily used to report income generated from political
                activities, investments, and other non-exempt sources. The form
                may also apply to tax-exempt organizations that have taxable
                political activity under{' '}
                <span style={{ fontWeight: 600 }}>Section 527(f)(1)</span>.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Who Must File Form 1120-POL?
              </Typography>

              <Typography component="h3" sx={{ fontSize: '16px' }}>
                The following organizations are required to file IRS Form
                1120-POL:
              </Typography>

              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 5 },
                  mt: { xs: 1, md: 4 },
                  mb: { xs: 0, md: 3 },
                  fontSize: '0.975rem',
                  '& li': { display: 'flex', mb: 2, ml: -2 },
                }}
              >
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>Political organizations described under Section 527</Box>
                </li>

                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>Political action committees (PACs)</Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>Campaign committees </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>Political parties </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    Separate segregated funds maintained by Section 501(c)
                    organizations{' '}
                  </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    Certain exempt organizations taxed under Section
                    527(f)(1){' '}
                  </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    Organizations with political organization taxable income
                  </Box>
                </li>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/who-must-file-1120-pol-img.webp" />
                  <source srcSet="/assets/images/who-must-file-1120-pol-img.png" />
                  <img
                    src="/assets/images/who-must-file-1120-pol-img.png"
                    alt="Who Must File Form 1120-POL?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={500}
                    height={550}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#ffffff',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Box
              // sx={{
              //   textAlign: 'center',
              //   alignItems: 'center',
              //   display: 'flex',
              //   justifyContent: 'center',
              // }}
              >
                {' '}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 2,
                    mt: 2,
                  }}
                  id="difference"
                >
                  Difference Between Political Organizations (527) and Taxable
                  Political Activity(527(f)(1))
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  borderRadius: 4,
                  minHeight: '100%',
                  backgroundColor: '#e3f2fd',
                  border: 'none',
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: { md: 24, sm: 20, xs: 18 },
                      mb: 3,
                    }}
                  >
                    Section 527{' '}
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 0 },
                      mt: { xs: 0, md: 4 },
                      mb: { xs: 0, md: 0 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    {' '}
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        Applies to political organizations made for elections or
                        campaigns.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        These groups are created mainly for political work.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <span style={{ fontWeight: 600 }}>
                        Examples: political parties, PACs, and campaign
                        committees.
                      </span>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        Used when reporting political organization income for
                        tax.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>Focus is fully on political activity.</Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>File 1120-POL when political income exists.</Box>
                    </li>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  borderRadius: 4,
                  minHeight: '100%',
                  backgroundColor: '#ccd5ae',
                  border: 'none',
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: { md: 24, sm: 20, xs: 18 },
                      mb: 3,
                    }}
                  >
                    Section 527(f)(1)
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 0 },
                      mt: { xs: 0, md: 4 },
                      mb: { xs: 0, md: 0 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    {' '}
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        Applies to normal tax-exempt organizations doing
                        political activity.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        These are usually nonprofit orgs doing some political
                        spending.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <span style={{ fontWeight: 600 }}>
                        Examples: 501(c) nonprofit organizations with political
                        activity.
                      </span>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        Used when political spending becomes taxable inside a
                        nonprofit.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        Focus is mainly nonprofit, but political activity is
                        taxed separately.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        File1120-POL if political activity creates taxable
                        income.
                      </Box>
                    </li>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',

          pt: 4,
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="xl" sx={{ mb: 0 }} className="image-gallery">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source srcSet="/assets/images/1120-pol-steps-img.webp" />
                <source srcSet="/assets/images/1120-pol-steps-img.png" />
                <img
                  src="/assets/images/1120-pol-steps-img.png"
                  alt="Information Required to File Form 1120-POL"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
              {/* <img
                              src={images[currentImageIndex]}
                              alt={`Image ${currentImageIndex + 1}`}
                              className="imagescroll"
                              loading="lazy"
                            /> */}
            </Box>

            <Box
              sx={{
                width: '100%',

                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mt: 3,
                  mb: 2,
                  textAlign: 'left',
                }}
              >
                Information Required to File Form 1120-POL
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 5 },
                  mt: { xs: 0, md: 4 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex', mb: 2 },
                }}
              >
                {' '}
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Organization details
                    </span>{' '}
                    (Name, EIN, address, tax year)
                  </Box>
                </li>
                <li>
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />{' '}
                  <Box>
                    <span style={{ fontWeight: 600 }}> Political income </span>{' '}
                    (contributions, taxable income, interest, etc.)
                  </Box>
                </li>
                <li>
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}> Expenses </span>
                    (operating and fundraising expenses)
                  </Box>
                </li>
                <li>
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      Taxable income calculation
                    </span>{' '}
                    (after deductions)
                  </Box>
                </li>
                <li>
                  {' '}
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Tax due and payments made{' '}
                    </span>
                    ({' '}
                    <Link href="/resources/eftps/" sx={{ color: '#0220ff' }}>
                      EFTPS
                    </Link>{' '}
                    or other deposits)
                  </Box>
                </li>{' '}
                <li>
                  {' '}
                  <TaskAltIcon sx={{ mr: 1, mb: -0.6, color: '#1da94a' }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Authorized signer details
                    </span>
                    (officer/treasurer)
                  </Box>
                </li>{' '}
              </Box>

              {/* <Typography variant="body1" sx={{ ml: 2, mt: 2 }}>
                  Filing again next year? TaxZerone saves your past filings so
                  you can reuse details and file faster.
                </Typography> */}
              <Box sx={{ ml: 2, mb: 4 }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 2,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1120pol`}
                >
                    Start E-Filing Now 
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Discover the features that make TaxZerone a trusted solution for
                political organizations and <br />
                exempt organizations filing Form 1120-POL accurately and
                efficiently.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-update-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-update-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-update-icon.png"
                      alt="IRS Status Updates"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Streamlined IRS Status Updates
                </Typography>
                <Typography variant="body1">
                  Receive real-time updates on your Form 1120-POL filing status
                  after transmitting your return through TaxZerone. Stay
                  informed throughout the entire e-filing process without the
                  need to wait for manual updates.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="IRS-Authorized E-Filing Software"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  IRS-Authorized E-Filing Software
                </Typography>
                <Typography variant="body1">
                  File your Form 1120-POL with confidence through TaxZerone, an
                  IRS-authorized e-file provider, and ensure your return is
                  securely submitted to the IRS.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/tax-platform-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/tax-platform-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/tax-platform-icon.png"
                      alt="User-Friendly"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  User-Friendly Filing Experience
                </Typography>
                <Typography variant="body1">
                  Access step-by-step guidance and helpful resources to complete
                  your filing accurately and efficiently. Our intuitive
                  interface and simple navigation make the e-filing process
                  quick and easy.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/paypal-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/paypal-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/paypal-icon.png"
                      alt="PayPal"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Pay Securely with PayPal
                </Typography>
                <Typography variant="body1">
                  Complete your filing with confidence using PayPal. Enjoy a
                  fast, secure, and flexible payment option without entering
                  your card details directly.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Retransmission"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Retransmission
                </Typography>
                <Typography variant="body1">
                  If your return is rejected by the IRS due to filing errors,
                  you can correct the issues and retransmit it at no additional
                  cost.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/accuracy-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/accuracy-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/accuracy-icon.png"
                      alt="Accurate and Compliant"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Accurate and Compliant
                </Typography>
                <Typography variant="body1">
                  Use our built-in IRS validation checks to identify errors
                  before submitting your Form 1120-POL. Improve filing accuracy
                  and reduce the chances of IRS rejections with a reliable and
                  thorough validation process.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Security at the Forefront"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Security at the Forefront
                </Typography>
                <Typography variant="body1">
                  File securely using a user-friendly e-filing platform.
                  Simplify IRS compliance and complete your filing accurately
                  through a guided electronic filing process.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/real-time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/real-time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/real-time-icon.png"
                      alt="Deadline Reminders"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Deadline Reminders
                </Typography>
                <Typography variant="body1">
                  Receive timely reminders before IRS filing deadlines to help
                  you stay organized, avoid last-minute filing delays, and
                  reduce the risk of penalties.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="K-1 Auto-Generation"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Expert Support
                </Typography>
                <Typography variant="body1">
                  Get assistance from experienced support professionals through
                  live chat, email, or phone. Support is available in both
                  English and Spanish to help with your filing needs.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Supporting Forms & Schedules for 1120-POL
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  TaxZerone currently supports the following schedules and
                  related forms for Form 1120-POL filing.
                </Typography>

                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mb: 3, color: '#6e6e6e' }}
                >
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/schedule-d-form-1120-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            Schedule D (Form 1120)
                          </Link>
                        </b>
                        &nbsp;– Capital Gains and Losses
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-4797-sales-business-property/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 4797
                          </Link>
                        </b>
                        &nbsp;– Sales of Business Property
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-4562-depreciation-amortization-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 4562
                          </Link>
                        </b>
                        &nbsp;– Depreciation and Amortization
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-3800-business-credits/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 3800
                          </Link>
                        </b>
                        &nbsp;– General Business Credit
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-8050-instructions/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8050
                          </Link>
                        </b>
                        &nbsp;– Direct Deposit of Tax Refund
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 7004
                          </Link>
                        </b>
                        &nbsp;– Application for Automatic Extension of Time to
                        File
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f8453te.pdf"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8453-TE
                          </Link>
                        </b>
                        &nbsp;– Tax-Exempt Entity Declaration and Signature for
                        E-file
                      </Box>
                    </li>

                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />
                      <Box>
                        <b>
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f8879te.pdf"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8879-TE
                          </Link>
                        </b>
                        &nbsp;– IRS E-file Signature Authorization for a
                        Tax-Exempt Entity
                      </Box>
                    </li>
                  </Box>
                </Typography>
                <Typography variant="body1">
                  Get detailed step-by-step guidance on completing Form 1120-POL
                  by referring to our{' '}
                  <Link
                    href="/resources/tax-exempt/form-1120-pol-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120-POL Instructions
                  </Link>{' '}
                  page.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/supporting-forms-1120-pol-img.webp" />
                  <source srcSet="/assets/images/supporting-forms-1120-pol-img.png" />
                  <img
                    src="/assets/images/supporting-forms-1120-pol-img.png"
                    alt="  Form 1120-POL Late Filing Penalties Explained"
                    className="img-responsive"
                    loading="lazy"
                    width={500}
                    height={436}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={7}
            mb={7}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'row', md: 'row' } }}
          >
            <Grid
              size={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'center',
                  md: 'center',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 2, md: 2 }, mt: { xs: -3, md: 1 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Form 1120-POL Filing Deadlines – 2025 Tax Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The filing deadline is the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 4th month after the end of the political
                    organization’s tax year.
                  </span>
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 700 }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: { xs: 2, md: 3 },
                  }}
                >
                  {/* Month Chevron Row */}

                  {/* Timeline */}
                  <Box
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      flexDirection: {
                        xs: 'column',
                        sm: 'row',
                        md: 'row',
                        lg: 'row',
                      },
                      alignItems: 'center',
                      flexWrap: {
                        xs: 'wrap',
                        sm: 'wrap',
                        md: 'wrap',
                        lg: 'nowrap',
                      },
                      rowGap: {
                        xs: 4,
                        sm: 4,
                        md: 4,
                        lg: 0,
                      },
                    }}
                  >
                    {/* Desktop Timeline Line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: { xs: 20, md: 60 },
                        right: { xs: 20, md: 0 },
                        maxWidth: { xs: 'calc(100% - 40px)', md: 400 },
                        height: 5,
                        background:
                          'linear-gradient(to right,#4A90D9,#c02652,#c54268,#3DB87A)',
                        display: {
                          xs: 'none',
                          sm: 'block',
                          md: 'block',
                          lg: 'block',
                        },
                      }}
                    />

                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          // border: '1.5px solid #3d77b8',
                          borderRadius: 2,
                          background: '#e8f0f8',
                          p: 1.5,
                          textAlign: 'center',
                          maxWidth: 150,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#2272B8',
                          }}
                        >
                          JAN 2026
                        </Typography>
                        <CalendarMonthIcon
                          sx={{ color: '#2272B8', fontSize: 22 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: 2,
                          height: 28,
                          background: '#3d95b8',
                        }}
                      />

                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: '#3d8bb8',
                          boxShadow: '0 0 0 3px rgba(61, 155, 184, 0.25)',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          // border: '1.5px solid #c02652',
                          borderRadius: 2,
                          background: '#f8e8f0',
                          p: 1.5,
                          textAlign: 'center',
                          maxWidth: 150,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#b82268',
                          }}
                        >
                          FEB 2026
                        </Typography>
                        <CalendarMonthIcon
                          sx={{ color: '#b82261', fontSize: 22 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: 2,
                          height: 28,
                          background: '#b83d85',
                        }}
                      />

                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: '#b83d6c',
                          boxShadow: '0 0 0 3px rgba(184, 61, 102, 0.25)',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          // border: '1.5px solid #c54268',
                          borderRadius: 2,
                          background: '#f8e8ed',
                          p: 1.5,
                          textAlign: 'center',
                          maxWidth: 150,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#b82268',
                          }}
                        >
                          MAR 2026
                        </Typography>
                        <CalendarMonthIcon
                          sx={{ color: '#b82261', fontSize: 22 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: 2,
                          height: 28,
                          background: '#b83d66',
                        }}
                      />

                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: '#b83d70',
                          boxShadow: '0 0 0 3px rgba(184, 61, 92, 0.25)',
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          // border: '1.5px solid #3DB87A',
                          borderRadius: 2,
                          background: '#E8F8F1',
                          p: 1.5,
                          textAlign: 'center',
                          maxWidth: 150,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#22b84f',
                          }}
                        >
                          APR 2026
                        </Typography>
                        <CalendarMonthIcon
                          sx={{ color: '#22b84f', fontSize: 22 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: 2,
                          height: 28,
                          background: '#3DB87A',
                        }}
                      />

                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: '#3DB87A',
                          boxShadow: '0 0 0 3px rgba(61,184,122,.25)',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      flexDirection: {
                        xs: 'column',
                        sm: 'row',
                        md: 'row',
                        lg: 'row',
                      },
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        ml: '-1px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          mt: 2,
                          color: '#2563EB',
                          fontWeight: 700,
                          fontSize: '18px',
                        }}
                      >
                        15th day
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#111827',
                          lineHeight: 1.2,
                        }}
                      >
                        of the 4th month after the tax year end
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                      }}
                    />

                    <Box
                      sx={{
                        flex: 1,
                      }}
                    />

                    <Box
                      sx={{
                        flex: 1,
                        ml: '-1px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          mt: 2,
                          color: '#16A34A',
                          fontWeight: 700,
                          fontSize: '18px',
                        }}
                      >
                        Apr 15, 2026
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: '#111827',
                        }}
                      >
                        Filing Deadline
                      </Typography>

                      {/* <Typography
                        sx={{
                          color: '#4B5563',
                          fontSize: '12px',
                        }}
                      >
                        (if extension is approved)
                      </Typography> */}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'left',
                  xs: 'center',
                },
              }}
            >
              <Box component="div" sx={{ mb: 4 }}>
                <TableContainer sx={{ mb: 4, border: '1px solid #828282' }}>
                  <Table aria-label="Due Date Chart">
                    <TableHead>
                      <TableRow
                        sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                      >
                        <TableCell sx={{ fontSize: '16px' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <DescriptionIcon fontSize="small" />
                            Filing Type
                          </Box>
                        </TableCell>

                        <TableCell sx={{ fontSize: '16px' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EventIcon fontSize="small" />
                            Due Date
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell>
                          <span style={{ fontWeight: 600, fontSize: '18px' }}>
                            Form 1120-POL
                          </span>
                          <br />
                          (calendar-year filers)
                        </TableCell>
                        <TableCell>April 15, 2026</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell>
                          <span style={{ fontWeight: 600, fontSize: '18px' }}>
                            Extension filing
                          </span>
                          <br />({' '}
                          <Link
                            href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 7004
                          </Link>{' '}
                          – must be filed by original due date)
                        </TableCell>
                        <TableCell>April 15, 2026</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell>
                          <span style={{ fontWeight: 600, fontSize: '18px' }}>
                            Extended filing deadline
                          </span>
                          <br />
                          (if extension is approved)
                        </TableCell>
                        <TableCell>October 15, 2026</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 0, borderLeft: '3px solid #ed6c02' }}
                >
                  <b>Notes:</b> If a due date falls on a weekend or federal
                  holiday, the deadline moves to the next business day.
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      {/* <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={2}
            mb={2}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'center',
                  md: 'center',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Form 1120-POL Filing Deadlines – 2025 Tax Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These due dates apply to political organizations operating on
                  a{' '}
                  <span style={{ fontWeight: 600 }}>
                    calendar year (January 1 – December 31)
                  </span>
                  .
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Filing Type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#fff' }}>
                          <TableCell>
                            IRS Form 1120-POL with the IRS (calendar-year
                            filers)
                          </TableCell>
                          <TableCell>April 15, 2026</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Standard filing deadline (fiscal-year filers)
                          </TableCell>
                          <TableCell>
                            15th day of the 4th month after year-end
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#fff' }}>
                          <TableCell>Extended filing (Form 7004)</TableCell>
                          <TableCell>October 15, 2026</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 0 }}>
                  <b>Important:</b> If a due date falls on a weekend or federal
                  holiday, the filing deadline moves to the next business day.
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid> */}

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 32, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Form 1120-POL Late Filing Penalties Explained
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 2,
                  }}
                >
                  If an organization is required to file Form 1120-POL and does
                  not submit it by the due date (including approved extensions),
                  the IRS may impose penalties and interest.
                </Typography>

                <TableContainer sx={{ mb: 4, border: '1px solid #828282' }}>
                  <Table aria-label="Penalty Chart">
                    <TableHead>
                      <TableRow
                        sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                      >
                        <TableCell
                          sx={{ color: '#444', width: 300, fontWeight: 600 }}
                        >
                          Penalty Type
                        </TableCell>

                        <TableCell
                          sx={{ color: '#444', width: 500, fontWeight: 600 }}
                        >
                          Description
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell>Late Filing</TableCell>
                        <TableCell>
                          5% of unpaid tax per month, up to 25%. Minimum penalty
                          may apply if filed more than 60 days late.
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell>Late Payment</TableCell>
                        <TableCell>
                          0.5% of unpaid tax per month, up to 25% of the unpaid
                          tax.
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell>Other Penalties</TableCell>
                        <TableCell>
                          Additional penalties may apply for negligence,
                          understatement of tax, or fraud. Interest may also
                          accrue.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/form-1120-pol-late-filing-penalites-img.webp" />
                  <source srcSet="/assets/images/form-1120-pol-late-filing-penalites-img.png" />
                  <img
                    src="/assets/images/form-1120-pol-late-filing-penalites-img.png"
                    alt="  Form 1120-POL Late Filing Penalties Explained"
                    className="img-responsive"
                    loading="lazy"
                    width={500}
                    height={550}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={7}
            mb={7}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/use-form-8050-img.webp" />
                  <source srcSet="/assets/images/use-form-8050-img.png" />
                  <img
                    src="/assets/images/use-form-8050-img.png"
                    alt="Use Form 8050 to Receive Your IRS Tax Refund"
                    className="img-responsive"
                    loading="lazy"
                    width={500}
                    height={600}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'left',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Use Form 8050 to Receive Your IRS Tax Refund
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone supports Form 8050 e-filing for eligible political
                  organizations, allowing refunds from Form 1120-POL filings to
                  be deposited directly into a bank account.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you need additional information or filing guidance, learn
                  more on our{' '}
                  <Link
                    href="/resources/tax-exempt/form-8050-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8050 instructions
                  </Link>{' '}
                  page.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={2}
            direction="row"
            justifyContent="center"
            className="MuiGrid-root-width"
            sx={{ my: { xs: 0, sm: 2 } }}
          >
            <Grid
              size={{ xs: 12, sm: 10, md: 9 }}
              textAlign="left"
              my={{ xs: 2, sm: 5 }}
              mb={3}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 5,
                  textAlign: 'center',
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Box
                component="div"
                sx={{ mb: 3 }}
                itemScope
                itemType="https://schema.org/FAQPage"
              >
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    1. What is Section 527(f)?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography variant="body1" sx={{ mb: 1, mt: 2, ml: 0 }}>
                        Section 527(f) refers to a rule under the Internal
                        Revenue Code that taxes certain political activities
                        conducted by otherwise tax-exempt organizations. Under
                        this section, organizations exempt from income tax may
                        be required to pay tax on income used for political
                        purposes or other non-exempt political activities.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    2. What is political organization taxable income? 
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography variant="body1" sx={{ mb: 2, mt: 2, ml: 0 }}>
                        Political organization taxable income generally includes
                        income that is not exempt under Section 527. This may
                        include investment income, certain advertising revenue,
                        fundraising income, or expenditures related to
                        non-exempt political activities. The taxable amount is
                        reported on IRS Form 1120-POL.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    3. Can I e-file Form 1120-POL through online tax services?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Box
                        component="ul"
                        sx={{
                          listStyleType: 'none',
                          paddingLeft: 0,
                          textAlign: 'left',
                          ml: { xs: 0, sm: 3 },
                          '& li': { display: 'flex' },
                        }}
                      >
                        <li
                          style={{
                            marginBottom: 10,
                          }}
                        >
                          <CheckCircleRoundedIcon
                            sx={{ color: '#1da94a', mr: 1 }}
                          />{' '}
                          <Box>
                            Yes. IRS Form 1120-POL can be electronically filed
                            through IRS-authorized online tax filing services.
                            These platforms help organizations submit returns
                            securely, reduce filing errors, and receive faster
                            IRS confirmation.
                          </Box>
                        </li>
                        <li
                          style={{
                            marginBottom: 10,
                          }}
                        >
                          <CheckCircleRoundedIcon
                            sx={{ color: '#1da94a', mr: 1 }}
                          />{' '}
                          <Box>
                            TaxZerone supports the secure electronic filing of
                            Form 1120-POL through IRS-authorized e-filing for
                            political organizations and exempt organization tax
                            forms.
                          </Box>
                        </li>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    4. Which exempt organizations must file Form 1120-POL?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>
                        Certain tax-exempt organizations may need to file Form
                        1120-POL if they have taxable political organization
                        income under Section 527(f)(1). This can include
                        organizations exempt under Section 501(c), labor
                        organizations, and other tax-exempt entities involved in
                        taxable political activities.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    5. What is Section 527 Political Organization?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>
                        Section 527 of the Internal Revenue Code defines
                        political organizations that are created primarily to
                        influence elections or political campaigns. These
                        include political parties, PACs, and campaign
                        committees, which may be required to file Form 1120-POL
                        if they have taxable income.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    6. Can I file an extension for Form 1120-POL online?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>
                        Yes. Political organizations can request an automatic
                        6-month extension to file Form 1120-POL by submitting{' '}
                        <span style={{ fontWeight: 600 }}>
                          IRS Form 7004 by the original due date
                        </span>
                        . With TaxZerone, you can complete and securely{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          e-file Form 7004 online
                        </Link>{' '}
                        to receive additional time to file your return.
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>
                        For detailed filing steps and instructions, visit our{' '}
                        <Link
                          href="/resources/tax-extension/form-7004-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 7004 instructions
                        </Link>{' '}
                        page.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form1120pol;
